import React, { useCallback } from 'react';
import { Workbook } from 'exceljs';
import { exportDataGrid } from 'devextreme/excel_exporter';
import FileSaver from 'file-saver';
import { DataGrid } from 'devextreme-react/data-grid';
import { ExportFormatType } from '@/Enums/ExportFormatType';

export const useOnExporting = <TData>(
  innerGridRef: React.MutableRefObject<DataGrid<TData, keyof TData> | undefined>,
  exportFileName: string,
  exportWorksheetName?: string,
  onExport?: (format: ExportFormatType, selectedItems: number[], visibleColumns: string[]) => void,
) => {
  const onExporting = useCallback(
    (format: ExportFormatType, onlySelected = false) => {
      if (!innerGridRef.current) return;

      if (onExport) {
        const selectedItems = innerGridRef.current?.instance.getSelectedRowKeys() as number[];
        const visibleColumns = innerGridRef.current?.instance
          .getVisibleColumns()
          .map((item) => item.dataField)
          .filter(Boolean) as string[];

        onExport(format, onlySelected ? selectedItems : [], visibleColumns);
      } else {
        localExport(format, onlySelected);
      }
    },
    [exportFileName, exportWorksheetName],
  );

  const localExport = (format: ExportFormatType, onlySelected = false) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(exportWorksheetName);

    const instance = innerGridRef.current?.instance;

    exportDataGrid({ component: instance, worksheet: worksheet, selectedRowsOnly: onlySelected }).then(() => {
      if (format == ExportFormatType.Csv) {
        workbook.csv.writeBuffer().then((buffer) => {
          FileSaver.saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${exportFileName}.csv`);
        });
      } else {
        workbook.xlsx.writeBuffer().then((buffer) => {
          FileSaver.saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${exportFileName}.xlsx`);
        });
      }
    });
  };

  return {
    onExporting,
  };
};
